import { AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import {
  Box,
  BoxNotifications,
  BoxWrapper,
  ButtonBell,
  SettingsWrapper,
} from './styles'

export interface DropdownOption {
  icon?: ReactNode
  title: string
  selected: boolean
  onSelect: () => void
}

interface DropdownProps {
  options: DropdownOption[]
  closeOnSelect?: boolean
  title: string | React.ReactChild
  width?: number
  listWidth?: number
  min?: boolean
}

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  title,
  closeOnSelect = false,
  width = 120,
  listWidth = 120,
  min = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [BellBoxMessageIsActive, setBoxMessageIsActive] = useState(false)

  // Referência para detectar clique fora
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fecha modal ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setBoxMessageIsActive(false)
        setIsOpen(false)

        // Simula clique no botão (caso necessário)
        const button = document.getElementById('my-button-id')
        if (button) {
          button.click()
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelect = (index: number) => {
    options[index].onSelect()
    if (closeOnSelect) {
      setIsOpen(false)
    }
  }

  return (
    <SettingsWrapper ref={dropdownRef}>
      <ButtonBell
        onClick={() => setBoxMessageIsActive(!BellBoxMessageIsActive)}
      >
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            border: min ? 'none' : '',
            boxShadow: min ? 'none' : '',
          }}
          className="flex active:bg-zinc-50 duration-100 border border-zinc-50 items-center gap-1 justify-center w-full rounded-md shadow-inner h-10 text-sm font-medium text-gray-500 outline-none focus:outline-none"
        >
          <span
            className="transition-all duration-200 ease-in-out"
            style={{
              rotate: typeof title !== 'string' && isOpen ? '25deg' : '',
            }}
          >
            {title}
          </span>
          {!min && (
            <ChevronDown
              size={15}
              color="#191919"
              strokeWidth={2.5}
              className="mt-[2px] duration-100"
              style={{
                rotate: isOpen ? '180deg' : '0deg',
              }}
            />
          )}
        </button>
      </ButtonBell>

      <AnimatePresence>
        {BellBoxMessageIsActive && (
          <BoxWrapper
            initial={{ opacity: 0, y: '-10%' }}
            animate={{ opacity: 1, y: '0%' }}
            exit={{ opacity: 0, y: '-10%' }}
            transition={{ duration: 0.2 }}
            style={{ zIndex: 99999 }}
          >
            <Box>
              <span />
              <BoxNotifications>
                <div
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="options-menu"
                  className="z-50"
                >
                  {options.map((option, index) => (
                    <button
                      key={index}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(index)}
                      className={`${
                        option.selected ? 'bg-gray-50 underline' : 'bg-white'
                      } hover:bg-zinc-600 radius rounded px-4 py-2 text-sm text-gray-700  hover:text-white w-full text-left flex flex-row justify-arround items-center gap-2`}
                      role="menuitem"
                    >
                      <span>{option.icon}</span> {/* Ícone */}
                      <span>{option.title}</span> {/* Título */}
                    </button>
                  ))}
                </div>
              </BoxNotifications>
            </Box>
          </BoxWrapper>
        )}
      </AnimatePresence>
    </SettingsWrapper>
  )
}
